import React, { useEffect, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { actions } from '../../state';
import { config } from '../../api/config';
import {
  formatTime,
  formatFilename,
  formatDate,
  mapServiceTypes,
  mapDiscountStatus,
  mapDiscountStatusColor,
} from '../../utils';
import {
  Header,
  HeaderActions,
  Title,
  Button,
  ButtonDownload,
  Table,
  TableRow,
  TableCell,
} from '../../components';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const Discounts = ({
  dispatch,
  discounts: { discounts, total, loading },
  xlsLoading,
}) => {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;
  const fetchDiscounts = useCallback(
    data => {
      dispatch(actions.discounts.getDiscountsAction(data));
    },
    [dispatch]
  );

  const clearDiscounts = useCallback(() => {
    dispatch(actions.discounts.clearDiscountsAction());
  }, [dispatch]);

  useEffect(() => () => clearDiscounts(), [clearDiscounts]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    fetchDiscounts({ offset });
  }, [fetchDiscounts, clearDiscounts, page]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const xlsExport = () => {
    const filename = formatFilename(config.PAGES.discounts);
    dispatch(actions.discounts.exportDiscountsAsyncAction(undefined, filename));
  };

  const renderTypes = types => {
    const list = types || [];
    if (!list.length) {
      return <p>Visos paslaugos</p>;
    }
    return list.map(type => <p key={type}>{mapServiceTypes(type)}</p>);
  };

  const renderLocations = locations =>
    locations ? (
      locations.map(location => <p key={location}>{location}</p>)
    ) : (
      <p>Visos lokacijos</p>
    );

  const renderTime = interval =>
    interval && (
      <p>
        {formatTime(interval[0])} - {formatTime(interval[1])}
      </p>
    );

  const tableHeader = [
    'Statusas',
    'Kodas',
    'Galioja nuo',
    'Galioja iki',
    'Laikas',
    'Lokacija',
    'Paslauga',
    'Panaudota',
    'Nuolaida',
  ];

  const renderTable = () =>
    discounts.map(
      ({
        id,
        status,
        code,
        start_date,
        end_date,
        time_interval,
        locations,
        service_types,
        use_count,
        used_count,
        discount,
      }) => (
        <TableRow url={config.APP_PATHS.editDiscount(id)} key={id}>
          <TableCell>
            <span style={{ color: mapDiscountStatusColor(status) }}>
              {mapDiscountStatus(status)}
            </span>
          </TableCell>
          <TableCell>{code}</TableCell>
          <TableCell>{formatDate(start_date)}</TableCell>
          <TableCell>{formatDate(end_date)}</TableCell>
          <TableCell>{renderTime(time_interval)}</TableCell>
          <TableCell>{renderLocations(locations)}</TableCell>
          <TableCell>{renderTypes(service_types)}</TableCell>
          <TableCell align="right">{used_count ?? use_count ?? 0}</TableCell>
          <TableCell align="right">{discount}%</TableCell>
        </TableRow>
      )
    );

  return (
    <section>
      <Header>
        <Title total={total}>{config.PAGES.discounts}</Title>
        <HeaderActions>
          <ButtonDownload onClick={xlsExport} loading={xlsLoading} />
          <Button color="primary" url={config.APP_PATHS.addDiscount}>
            Sukurti
          </Button>
        </HeaderActions>
      </Header>

      <Table
        header={tableHeader}
        isEmpty={!discounts.length}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </section>
  );
};

const mapStateToProps = state => ({
  discounts: state.discounts,
  xlsLoading: state.download.xlsLoading,
});

Discounts.propTypes = {
  dispatch: PropTypes.func.isRequired,
  discounts: PropTypes.shape({
    discounts: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  xlsLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(Discounts);
